import { VIDEO_FILES_QUERY } from '../gql/media-gql';
import { normalizePlacementMeta } from '../repositories/normalizePlacementMeta';
import { AspectRatio, VideoStatus } from '../types.ts/video';
import { extractErrorInfo } from '../utility/error';
import { randomString } from '../utility/general';
import { LogManager } from '../utility/Logger';
import { derivePunchList } from '../utility/punchList';

import type ApiClient from '../apiClient/ApiClient';
import type {
  Video,
  VideoFilesQueryResult,
  VideoItem,
  VideoVersion,
} from '../types.ts/story';
import type { TranscriptChange } from '../types.ts/transcript';
import type { VideoResourcesMetadata } from '../types.ts/video';
import type {
  Client,
  SchemaTypes,
  SimpleSchemaTypes,
} from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

type TranscriptStabilityRefactorMigrationInfo = {
  transcriptionMigrationStatus?:
    | 'ok'
    | 'warning'
    | 'error'
    | 'not_supported'
    | 'none';
  transcriptionMigrationLog?: object;
};

type VideoVersionDTO = Video & { versionId?: string } & {
  editor?: SimpleSchemaTypes.User | SimpleSchemaTypes.ItemVersion['editor'];
} & {
  transcriptionText?: string;
  resourcesMetadata?: VideoResourcesMetadata;
} & TranscriptStabilityRefactorMigrationInfo;

type DatoVideoVersion = SimpleSchemaTypes.ItemVersion &
  VideoItem & { versionId: string };

type DatoVideoVersionsResponse = {
  videoVersions: DatoVideoVersion[];
  totalCount: number;
};

const PRIMARY_ASPECT_RATIO = AspectRatio.AR_16_9;
export const MAX_RECORD_SIZE = 1_100_000;

const logger = LogManager.getLogger('repository:VideoRepository');

const mapItemVideoVersion = (
  item: SchemaTypes.ItemVersion,
): DatoVideoVersion => {
  const { attributes, relationships, ...rest } = item;
  return {
    ...attributes,
    ...rest,
    item: relationships.item.data,
    item_type: relationships.item_type.data,
    editor: relationships.editor.data,
  } as DatoVideoVersion;
};

export class VideoRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  async getVersionsByVideoId(
    id: string,
    pageLimit: number,
    page: number = 0,
    sizeAware: boolean = false,
  ): Promise<{ videoVersions: VideoVersion[]; totalCount: number }> {
    const data = sizeAware
      ? await this.loadVersionsInParallel(id, pageLimit, page)
      : await this.loadVersionsAllAtOnce(id, pageLimit, page);
    const videoVersions = await this.mapVideoVersions(id, data.videoVersions);
    return {
      videoVersions,
      totalCount: data.totalCount,
    };
  }

  private async mapVideoVersions(
    id: string,
    videoVersions: DatoVideoVersion[],
  ): Promise<VideoVersion[]> {
    const editors: any = {};
    videoVersions.forEach(videoVersion => {
      editors[videoVersion.editor.type + videoVersion.editor.id] =
        videoVersion.editor;
    });

    // change to fetch associated videos
    const latestVersionVideoFiles = (
      (await this.gqlClient.request(VIDEO_FILES_QUERY, {
        id,
      })) as VideoFilesQueryResult
    ).video!;

    await Promise.all(
      Object.keys(editors).map(async key => {
        try {
          const user = await this.dClient.users.find(editors[key]);
          editors[key] = user;
        } catch (err) {
          // Editor user was deleted from DatoCMS (or never existed for this
          // env's data). Non-fatal — the version row just renders without an
          // editor name. Logged at warn for debuggability without spamming
          // the console as a red error.
          logger.warn('Editor user not found in DatoCMS — suppressed', {
            editorKey: key,
            ...extractErrorInfo(err),
          });
        }
      }),
    );

    const videoVersionsMapped: VideoVersion[] = videoVersions.map(
      videoVersion => {
        // PLAT-10921 E11 — normalize the legacy `manuallyPositioned` flag into
        // the new placement fields once, here, so every layer above speaks only
        // the new vocabulary. `manuallyPositioned` lives inside this blob, so no
        // DatoCMS migration can reach it. See `normalizePlacementMeta`.
        let extraElementData: Video['extraElementData'] =
          normalizePlacementMeta(
            JSON.parse(videoVersion.extra_elements_json) || {},
          );

        const punchList = derivePunchList(extraElementData);

        const parentVideoQueryResult =
          latestVersionVideoFiles._allReferencingVideos?.at(0);
        const subtitles = JSON.parse(videoVersion.subtitles_json);
        return {
          ...videoVersion,
          // title: videoVersion.title,
          versionId: videoVersion.id,
          id: id,
          subtitles:
            !subtitles || Object.keys(subtitles).length === 0
              ? null
              : subtitles,
          resourcesMetadata: JSON.parse(
            videoVersion.resources_metadata_json ?? '{}',
          ),
          videoSource: JSON.parse(videoVersion.video_json),
          videoFilePrimary: videoVersion.meta.is_current
            ? latestVersionVideoFiles.videoFilePrimary
            : undefined,
          associatedVideos: latestVersionVideoFiles.associatedVideos,
          parentVideo: parentVideoQueryResult
            ? {
                ...parentVideoQueryResult,
                videoSource: parentVideoQueryResult.videoJson,
                videoJson: undefined,
              }
            : undefined,
          aspectRatio: videoVersion.aspect_ratio,
          transcriptionChanges: (videoVersion.transcription_json
            ? JSON.parse(videoVersion.transcription_json).changelog
            : []) as TranscriptChange[],
          transcriptionSnapshot: JSON.parse(
            videoVersion.transcription_snapshot_json,
          ),
          transcriptionText: videoVersion.transcription_text,
          punchList,
          extraElementData,
          videoStatus: videoVersion.video_status,
          editor: editors[videoVersion.editor.type + videoVersion.editor.id],
          thumbnail: latestVersionVideoFiles?.thumbnail,
          sourcePlatform: videoVersion.source_platform,
          clipJson: videoVersion.clip_json
            ? JSON.parse(videoVersion.clip_json)
            : {},
          reviewStatus: videoVersion.review_status,
          assignee: videoVersion.assignee,
          isClientReady: videoVersion.is_client_ready,
          isDownloadEnabled: videoVersion.is_download_enabled,
          isHidden: videoVersion.is_hidden,
          lastActionJson: videoVersion.last_action_json
            ? JSON.parse(videoVersion.last_action_json)
            : {},
          _publishedAt: videoVersion.meta.created_at,
        };
      },
    );

    return videoVersionsMapped;
  }

  private async loadVersionsInParallel(
    id: string,
    pageLimit: number,
    page: number = 0,
  ): Promise<DatoVideoVersionsResponse> {
    const firstItemToLoad = page * pageLimit;
    const totalItemsToLoad = firstItemToLoad + pageLimit;
    let totalCount = 0;
    const videoVersions = (
      await Promise.all(
        Array(totalItemsToLoad - firstItemToLoad)
          .fill('')
          .map(async (_, i) => {
            try {
              let { data, meta } = await this.dClient.itemVersions.rawList(id, {
                nested: true,
                page: {
                  limit: 1,
                  offset: firstItemToLoad + i,
                },
              });
              const [item] = data;
              if (!i) totalCount = meta.total_count;
              if (!item) return null;
              return mapItemVideoVersion(item);
            } catch (err) {
              console.error('Failed to fetch video version', err);
            }
          }),
      )
    ).filter(Boolean) as DatoVideoVersion[];
    return { videoVersions, totalCount };
  }

  private async loadVersionsAllAtOnce(
    id: string,
    pageLimit: number,
    page: number = 0,
  ): Promise<DatoVideoVersionsResponse> {
    const response = await this.dClient.itemVersions.rawList(id, {
      nested: true,
      page: {
        limit: pageLimit,
        offset: page * pageLimit,
      },
    });
    const videoVersions = response.data.map(mapItemVideoVersion);
    return { videoVersions, totalCount: response.meta.total_count };
  }

  /**
   * Save a lightweight clip with minimal data - no S3 resources.
   * Source representation will be generated lazily when the clip is loaded.
   */
  async saveLightweightVideo(video: {
    title?: string;
    aspectRatio?: string;
    sourcePlatform?: string;
    clipJson?: object;
    lastActionJson?: object;
    isClientReady?: boolean;
    transcriptionText?: string;
  }): Promise<{ id: string; slug: string; hash: string }> {
    console.time('saveLightweightVideo:findItemType');
    const itemType = await this.dClient.itemTypes.find('video');
    console.timeEnd('saveLightweightVideo:findItemType');

    console.time('saveLightweightVideo:createItem');
    const savedVideo = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      title: video.title,
      hash: randomString(12),
      slug: randomString(12),
      video_json: null,
      resources_metadata_json: JSON.stringify({}),
      transcription_snapshot_json: null,
      transcription_json: JSON.stringify({ changelog: [] }),
      transcription_text: video.transcriptionText ?? '',
      extra_elements_json: JSON.stringify({}),
      subtitles_json: JSON.stringify({}),
      aspect_ratio: video.aspectRatio || PRIMARY_ASPECT_RATIO,
      source_platform: video.sourcePlatform,
      clip_json: JSON.stringify(video.clipJson || {}),
      is_client_ready: video.isClientReady,
      last_action_json: JSON.stringify(video.lastActionJson || {}),
      video_status: VideoStatus.Editing,
    });
    console.timeEnd('saveLightweightVideo:createItem');

    // Publish the video so it appears in queries
    console.time('saveLightweightVideo:publish');
    await this.dClient.items.publish(savedVideo.id);
    console.timeEnd('saveLightweightVideo:publish');

    return {
      id: savedVideo.id,
      slug: savedVideo.slug as string,
      hash: savedVideo.hash as string,
    };
  }

  async saveOrUpdateVideo(video: Partial<VideoVersionDTO>): Promise<{
    id: string;
    slug: string;
    hash: string;
    /** The Dato version this save created (PLAT-11911). */
    versionId?: string;
  }> {
    let videoData:
      | { id: string; slug: string; hash: string; versionId?: string }
      | undefined;

    if (video.id) {
      videoData = await this.updateVideo(
        video as Partial<VideoVersionDTO> & { id: string },
      );
    } else {
      videoData = await this.createVideo(
        video as Partial<VideoVersionDTO> & { id: undefined },
      );
    }
    return videoData;
  }

  async copyParentAndAssociatedVideos(
    videoIdOrItem: string | VideoItem,
    newTitle?: string,
    sourcePlatform?: Video['sourcePlatform'],
    lastActionJson?: string,
  ): Promise<{
    id: string;
    slug: string;
    hash: string;
    associatedVideoIdPairs: { sourceId: string; copyId: string }[];
  }> {
    const parentVideoCopy =
      typeof videoIdOrItem === 'string'
        ? ((await this.dClient.items.duplicate(
            videoIdOrItem,
          )) as unknown as VideoItem)
        : videoIdOrItem; // already pre-created copy
    const associatedVideoIdPairs: { sourceId: string; copyId: string }[] = [];
    for (const associatedVideoId of parentVideoCopy.associated_videos) {
      const associatedVideoCopy = (await this.dClient.items.duplicate(
        associatedVideoId,
      )) as unknown as VideoItem;
      await this.dClient.items.update(associatedVideoCopy.id, {
        slug: randomString(12),
        hash: randomString(12),
        title: newTitle || associatedVideoCopy.title,
        source_platform: sourcePlatform || associatedVideoCopy.source_platform,
        video_file_primary: null,
        video_status: VideoStatus.Editing,
        last_action_json:
          lastActionJson || associatedVideoCopy.last_action_json,
      });
      associatedVideoIdPairs.push({
        sourceId: associatedVideoId,
        copyId: associatedVideoCopy.id,
      });
    }
    const parentSlug = randomString(12);
    const parentHash = randomString(12);
    await this.dClient.items.update(parentVideoCopy.id, {
      slug: parentSlug,
      hash: parentHash,
      associated_videos: associatedVideoIdPairs.map(v => v.copyId),
      title: newTitle || parentVideoCopy.title,
      source_platform: sourcePlatform || parentVideoCopy.source_platform,
      video_file_primary: null,
      video_status: VideoStatus.Editing,
      last_action_json: lastActionJson || parentVideoCopy.last_action_json,
    });
    return {
      id: parentVideoCopy.id,
      slug: parentSlug,
      hash: parentHash,
      associatedVideoIdPairs: associatedVideoIdPairs,
    };
  }

  estimateRecordSize(video: Partial<VideoVersionDTO>): number {
    const transcription_snapshot_json = JSON.stringify(
      video.transcriptionSnapshot || {},
    );
    const video_source_json = JSON.stringify(video.videoSource || {});
    const subtitles_json = JSON.stringify(video.subtitles || {});
    const extra_elements_json = JSON.stringify(video.extraElementData || {});
    const title = video.title || '';
    const transcription_text = video.transcriptionText || '';

    const estimatedSize =
      1.5 *
      (new Blob([video_source_json.replaceAll('"', '\\"')]).size +
        new Blob([subtitles_json.replaceAll('"', '\\"')]).size +
        new Blob([extra_elements_json.replaceAll('"', '\\"')]).size +
        new Blob([title]).size +
        new Blob([transcription_text]).size +
        new Blob([transcription_snapshot_json.replaceAll('"', '\\"')]).size);
    return estimatedSize;
  }

  async updateVideo(
    video: Partial<VideoVersionDTO> & { id: string },
  ): Promise<{ id: string; slug: string; hash: string; versionId?: string }> {
    const transcription_snapshot_json = JSON.stringify(
      video.transcriptionSnapshot,
    );
    const recordSize = this.estimateRecordSize(video);
    const isOverSizeLimit = recordSize >= MAX_RECORD_SIZE;

    if (isOverSizeLimit) {
      logger.warn(
        'Record size exceeds limit, omitting transcription snapshot and text',
        {
          videoId: video.id,
          estimatedSize: recordSize,
          limit: MAX_RECORD_SIZE,
        },
      );
    }

    const savedVideo = await this.dClient.items.update(video.id, {
      ...(video.resourcesMetadata && {
        resources_metadata_json: JSON.stringify(video.resourcesMetadata),
      }),

      ...(video.resourcesMetadata?.videoSourceSnapshot && {
        video_json: null, // moved to S3
      }),

      ...(video.subtitles && {
        subtitles_json: JSON.stringify(video.subtitles || {}),
      }),

      ...(video.title && { title: video.title }),

      ...(video.transcriptionChanges && {
        transcription_json: JSON.stringify({
          changelog: video.transcriptionChanges,
        }),
      }),

      ...(video.transcriptionSnapshot &&
        !isOverSizeLimit && {
          transcription_snapshot_json,
          transcription_json: JSON.stringify({
            changelog: [],
          }),
        }),

      ...(video.transcriptionMigrationStatus && {
        transcription_migration_status: video.transcriptionMigrationStatus,
      }),

      ...(video.transcriptionMigrationLog && {
        transcription_migration_log: JSON.stringify(
          video.transcriptionMigrationLog,
        ),
      }),

      ...(video.transcriptionText &&
        !isOverSizeLimit && {
          transcription_text: video.transcriptionText,
        }),

      ...(video.extraElementData && {
        extra_elements_json: JSON.stringify(video.extraElementData),
      }),

      ...(video.thumbnail && {
        thumbnail: {
          upload_id: video.thumbnail.id,
        },
      }),

      ...(video.aspectRatio && {
        aspect_ratio: video.aspectRatio,
      }),

      ...(video.associatedVideos && {
        associated_videos: video.associatedVideos.map(v => v.id),
      }),

      ...(video.isClientReady != null && {
        is_client_ready: video.isClientReady,
      }),

      ...(video.isDownloadEnabled != null && {
        is_download_enabled: video.isDownloadEnabled,
      }),

      ...(video.isHidden != null && {
        is_hidden: video.isHidden,
      }),

      ...(video.clipJson && {
        clip_json: JSON.stringify(video.clipJson),
      }),

      ...(video.reviewStatus != null && {
        review_status: video.reviewStatus,
      }),

      ...(video.assignee != null && {
        assignee: video.assignee,
      }),

      ...(video.lastActionJson && {
        last_action_json: JSON.stringify(video.lastActionJson),
      }),
    });
    // auto-publish
    return {
      id: savedVideo.id,
      slug: savedVideo.slug as string,
      hash: savedVideo.hash as string,
      versionId: savedVideo.meta.current_version,
    };
  }

  async createVideo(
    video: Partial<VideoVersionDTO> & { id: undefined },
  ): Promise<{ id: string; slug: string; hash: string; versionId?: string }> {
    const extraElementData = video.extraElementData || {};
    video.punchList?.forEach(punchListItem => {
      extraElementData[punchListItem.id!] = {
        ...(extraElementData[punchListItem.id!] || {}),
        punchListData: punchListItem,
      };
    });

    const transcription_snapshot_json = JSON.stringify(
      video.transcriptionSnapshot,
    );
    const recordSize = this.estimateRecordSize(video);
    const isOverSizeLimit = recordSize >= MAX_RECORD_SIZE;

    if (!video.aspectRatio) {
      console.error(
        'Creating new video without aspect ratio, using fallback',
        PRIMARY_ASPECT_RATIO,
      );
    }

    if (isOverSizeLimit) {
      logger.warn(
        'Record size exceeds limit, omitting transcription snapshot and text',
        {
          title: video.title,
          estimatedSize: recordSize,
          limit: MAX_RECORD_SIZE,
        },
      );
    }

    const itemType = await this.dClient.itemTypes.find('video');
    // TODO add type check for datocms video item type
    const savedVideo = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      title: video.title,
      hash: randomString(12),
      slug: randomString(12),
      video_json: null, // moved to S3
      resources_metadata_json: JSON.stringify(video.resourcesMetadata || {}),
      subtitles_json: JSON.stringify(video.subtitles || {}),
      transcription_json: JSON.stringify({
        changelog: video.transcriptionChanges,
      }),
      ...(video.transcriptionSnapshot &&
        !isOverSizeLimit && {
          transcription_snapshot_json,
          transcription_json: JSON.stringify({
            changelog: [],
          }),
        }),
      ...(video.transcriptionMigrationStatus && {
        transcription_migration_status: video.transcriptionMigrationStatus,
      }),

      ...(video.transcriptionMigrationLog && {
        transcription_migration_log: JSON.stringify(
          video.transcriptionMigrationLog,
        ),
      }),
      transcription_text: isOverSizeLimit ? '' : video.transcriptionText || '',
      extra_elements_json: JSON.stringify(extraElementData),
      aspect_ratio: video.aspectRatio || PRIMARY_ASPECT_RATIO,
      associated_videos: video.associatedVideos?.map(v => v.id),
      source_platform: video.sourcePlatform,
      clip_json: JSON.stringify(video.clipJson || {}),
      is_client_ready: video.isClientReady,
      is_download_enabled: video.isDownloadEnabled,
      is_hidden: video.isHidden,
      last_action_json: JSON.stringify(video.lastActionJson || {}),
    });
    // TODO other fields
    // auto-publish
    return {
      id: savedVideo.id,
      slug: savedVideo.slug as string,
      hash: savedVideo.hash as string,
      versionId: savedVideo.meta.current_version,
    };
  }

  async updateThumbnail(
    id: string,
    thumbnailId: string,
    shareableImageId: string | undefined,
  ): Promise<string> {
    const savedVideo = await this.dClient.items.update(id, {
      thumbnail: {
        upload_id: thumbnailId,
      },
      shareable_image_id: shareableImageId || '',
    });
    return savedVideo.id;
  }

  async findVideo(id: string): Promise<VideoItem | null> {
    try {
      const video = await this.dClient.items.find(id);
      return video as unknown as VideoItem;
    } catch (err) {
      console.error('Failed to find video', err);
      return null;
    }
  }
}
